import { doGET, doPOST, doPUT } from '../../utils/HttpUtils';
import { IExternalBooking } from '../../types/bookings';
import { BulkCampOrderResult, Camp, CampDetails, CampSlot, PlaceCampOrderPayload } from '../../types/camps';
import { showError, showSuccess } from '../toaster';

const BASE = '/camps';

function unwrap<T>(res: any): T | null {
    if (res?.error || res?.status >= 400) {
        showError(res?.message || res?.data?.message || 'Camp request failed');
        return null;
    }

    return (res?.data?.data ?? res?.data ?? null) as T | null;
}

export async function fetchCamps(params: { startDate?: string; endDate?: string; test?: boolean; corporateId?: string } = {}) {
    const q = new URLSearchParams();
    if (params.startDate?.trim()) q.set('startDate', params.startDate.trim());
    if (params.endDate?.trim()) q.set('endDate', params.endDate.trim());
    if (params.test) q.set('test', 'true');
    if (params.corporateId?.trim()) q.set('corporateId', params.corporateId.trim());

    const res = await doGET(`${BASE}${q.toString() ? `?${q.toString()}` : ''}`);
    return unwrap<Camp[]>(res) || [];
}

export async function fetchCampDetails(campLsId: string) {
    const res = await doGET(`${BASE}/${encodeURIComponent(campLsId)}`);
    return unwrap<CampDetails>(res);
}

export async function fetchCampSlots(campLsId: string) {
    const res = await doGET(`${BASE}/${encodeURIComponent(campLsId)}/slots`);
    return unwrap<CampSlot[]>(res) || [];
}

export async function fetchCampBookings(params: {
    bookingDateFrom?: string;
    bookingDateTo?: string;
    page?: number;
    limit?: number;
    corporateId?: string;
} = {}) {
    const q = new URLSearchParams({
        integration: 'labstack',
        providerType: 'CAMP',
    });
    if (params.bookingDateFrom) q.set('bookingDateFrom', params.bookingDateFrom);
    if (params.bookingDateTo) q.set('bookingDateTo', params.bookingDateTo);
    if (params.page) q.set('page', String(params.page));
    if (params.limit) q.set('limit', String(params.limit));
    if (params.corporateId) q.set('corporateId', params.corporateId);

    const res = await doGET(`/external-bookings/bookings?${q.toString()}`);
    const rows = unwrap<IExternalBooking[]>(res) || [];
    const pagination = res?.data?.pagination || {};

    return {
        rows,
        total: pagination.total ?? rows.length,
        page: pagination.page ?? params.page ?? 1,
        totalPages: pagination.totalPages ?? 1,
    };
}

export async function placeCampOrder(payload: PlaceCampOrderPayload) {
    const res = await doPOST(`${BASE}/orders`, payload);
    const data = unwrap<any>(res);
    if (data) {
        showSuccess('Camp order placed successfully');
    }
    return data;
}

export async function updateCampCorporateIds(campLsId: string, corporateIds: string[]) {
    const res = await doPUT(`${BASE}/${encodeURIComponent(campLsId)}/corporate-ids`, { corporateIds });
    const data = unwrap<{ campLsId: string; corporateIds: string[] }>(res);
    if (data) {
        showSuccess('Camp visibility updated');
    }
    return data;
}

export async function bulkPlaceCampOrders(payload: {
    campLsId: string;
    orderType?: string;
    appointment: string;
    file: File;
    packageIds?: string[];
    testIds?: string[];
    isVip?: boolean;
    labId?: string;
    pincode?: string;
    labName?: string;
    notes?: string;
}) {
    const formData = new FormData();
    formData.append('file', payload.file);
    formData.append('campLsId', payload.campLsId);
    formData.append('orderType', payload.orderType || 'CENTER_VISIT');
    formData.append('appointment', payload.appointment);
    if (payload.packageIds?.length) formData.append('packageIds', payload.packageIds.join(','));
    if (payload.testIds?.length) {
        formData.append('testIds', payload.testIds.join(','));
    }
    if (payload.isVip !== undefined) formData.append('isVip', String(payload.isVip));
    if (payload.labId) formData.append('labId', payload.labId);
    if (payload.pincode) formData.append('pincode', payload.pincode);
    if (payload.labName) formData.append('labName', payload.labName);
    if (payload.notes) formData.append('notes', payload.notes);

    const res = await doPOST(`${BASE}/orders/bulk`, formData);
    const data = unwrap<BulkCampOrderResult>(res);
    if (data) {
        showSuccess(`Camp bulk booking completed: ${data.createdCount} created, ${data.failedCount} failed`);
    }
    return data;
}
